/**
 * The 28 modular hero slots, and the measured facts about each one.
 *
 * Every count, filename token and index base below was measured directly from
 * `art-source/POLYGON_ModularFantasyHero/FBX`, not read off the pack's marketing
 * material. The slot list is provably complete: the per-slot file counts sum to
 * exactly the 720 `SK_Chr_*.fbx` files the pack ships, with nothing unaccounted for
 * (see `EXPECTED_HERO_OPTION_COUNTS` and `verifyHeroSlotCounts`).
 *
 * See develop/ModularCharacter.md §2, and develop/ArtLibrary.md §4 for the raw pack.
 */
import type { Gender } from './definition.ts';

export const HERO_SLOTS = [
  // Core body. A character is incomplete without these.
  'Torso',
  'Hips',
  'Head',
  'HeadNoElements',
  'ArmUpperLeft',
  'ArmUpperRight',
  'ArmLowerLeft',
  'ArmLowerRight',
  'HandLeft',
  'HandRight',
  'LegLeft',
  'LegRight',
  // Face and hair.
  'Hair',
  'FacialHair',
  'Eyebrow',
  'Ear',
  // Head coverings -- mutually constrained with hair and facial hair.
  'HelmetAttachment',
  'HeadCoveringsBaseHair',
  'HeadCoveringsNoHair',
  'HeadCoveringsNoFacialHair',
  // Equipment attachments.
  'ShoulderAttachLeft',
  'ShoulderAttachRight',
  'ElbowAttachLeft',
  'ElbowAttachRight',
  'KneeAttachLeft',
  'KneeAttachRight',
  'BackAttachment',
  'HipsAttachment',
] as const;
export type HeroSlot = (typeof HERO_SLOTS)[number];

/**
 * How a head covering interacts with hair and facial hair. Inferred from the pack's
 * naming; needs visual confirmation (step 3.3) before the editor enforces it hard.
 */
export type HeadCoveringRule = 'overHair' | 'requiresNoHair' | 'requiresNoFacialHair';

export interface SlotMeta {
  /**
   * True when the pack ships a separate male and female mesh. Gendered parts are
   * NOT interchangeable -- the mesh shapes differ even though the skeleton does not
   * -- so gender filters the whole catalogue rather than being a slot in its own
   * right (§7.3).
   */
  gendered: boolean;
  /**
   * Options offered to a character of each gender. For a shared slot both numbers
   * count the same files; for `FacialHair`, female is 0.
   */
  variants: Readonly<Record<Gender, number>>;
  /**
   * First index used in the source filenames. Core body slots are 0-based and the
   * rest are 1-based; ranges are contiguous. Measured, and easy to get wrong.
   */
  firstIndex: 0 | 1;
  /** Filename token between the `SK_Chr_` prefix and the gender/index suffix. */
  token: string;
  /**
   * `body` merges into the single skinned mesh; `attachment` stays a separate mesh
   * parented to the skeleton. Re-merging is fine in a character creator and
   * unacceptable mid-combat, so anything that can change during a run is an
   * attachment (§7.3).
   */
  merge: 'body' | 'attachment';
  /** A complete body needs this slot filled. */
  required: boolean;
  /** Choosing this slot forbids choosing the named one. */
  exclusiveWith?: HeroSlot;
  /** Set only on the three head-covering slots. */
  headCovering?: HeadCoveringRule;
}

export const SLOT_META: Readonly<Record<HeroSlot, SlotMeta>> = {
  Torso: { gendered: true, variants: { male: 29, female: 29 }, firstIndex: 0, token: 'Torso', merge: 'body', required: true },
  Hips: { gendered: true, variants: { male: 29, female: 29 }, firstIndex: 0, token: 'Hips', merge: 'body', required: true },
  // `Head` carries facial features; `Head_No_Elements` is a bare head for use under
  // a full helmet. Exactly one of the two is worn -- see `validateSlotSelection`.
  Head: { gendered: true, variants: { male: 23, female: 23 }, firstIndex: 0, token: 'Head', merge: 'body', required: true, exclusiveWith: 'HeadNoElements' },
  HeadNoElements: { gendered: true, variants: { male: 13, female: 13 }, firstIndex: 1, token: 'Head_No_Elements', merge: 'body', required: false, exclusiveWith: 'Head' },
  ArmUpperLeft: { gendered: true, variants: { male: 21, female: 21 }, firstIndex: 0, token: 'ArmUpperLeft', merge: 'body', required: true },
  ArmUpperRight: { gendered: true, variants: { male: 21, female: 21 }, firstIndex: 0, token: 'ArmUpperRight', merge: 'body', required: true },
  ArmLowerLeft: { gendered: true, variants: { male: 19, female: 19 }, firstIndex: 0, token: 'ArmLowerLeft', merge: 'body', required: true },
  ArmLowerRight: { gendered: true, variants: { male: 19, female: 19 }, firstIndex: 0, token: 'ArmLowerRight', merge: 'body', required: true },
  HandLeft: { gendered: true, variants: { male: 18, female: 18 }, firstIndex: 0, token: 'HandLeft', merge: 'body', required: true },
  HandRight: { gendered: true, variants: { male: 18, female: 18 }, firstIndex: 0, token: 'HandRight', merge: 'body', required: true },
  LegLeft: { gendered: true, variants: { male: 20, female: 20 }, firstIndex: 0, token: 'LegLeft', merge: 'body', required: true },
  LegRight: { gendered: true, variants: { male: 20, female: 20 }, firstIndex: 0, token: 'LegRight', merge: 'body', required: true },

  Hair: { gendered: false, variants: { male: 38, female: 38 }, firstIndex: 1, token: 'Hair', merge: 'body', required: false },
  // The only single-gender slot in the pack: there is no female facial hair mesh.
  FacialHair: { gendered: true, variants: { male: 18, female: 0 }, firstIndex: 1, token: 'FacialHair', merge: 'body', required: false },
  Eyebrow: { gendered: true, variants: { male: 10, female: 7 }, firstIndex: 1, token: 'Eyebrow', merge: 'body', required: false },
  // Filenames double the token: `SK_Chr_Ear_Ear_01.fbx`.
  Ear: { gendered: false, variants: { male: 3, female: 3 }, firstIndex: 1, token: 'Ear_Ear', merge: 'body', required: false },

  HelmetAttachment: { gendered: false, variants: { male: 13, female: 13 }, firstIndex: 1, token: 'HelmetAttachment', merge: 'attachment', required: false },
  HeadCoveringsBaseHair: { gendered: false, variants: { male: 11, female: 11 }, firstIndex: 1, token: 'HeadCoverings_Base_Hair', merge: 'attachment', required: false, headCovering: 'overHair' },
  HeadCoveringsNoHair: { gendered: false, variants: { male: 13, female: 13 }, firstIndex: 1, token: 'HeadCoverings_No_Hair', merge: 'attachment', required: false, headCovering: 'requiresNoHair' },
  HeadCoveringsNoFacialHair: { gendered: false, variants: { male: 4, female: 4 }, firstIndex: 1, token: 'HeadCoverings_No_FacialHair', merge: 'attachment', required: false, headCovering: 'requiresNoFacialHair' },

  ShoulderAttachLeft: { gendered: false, variants: { male: 21, female: 21 }, firstIndex: 1, token: 'ShoulderAttachLeft', merge: 'attachment', required: false },
  ShoulderAttachRight: { gendered: false, variants: { male: 21, female: 21 }, firstIndex: 1, token: 'ShoulderAttachRight', merge: 'attachment', required: false },
  ElbowAttachLeft: { gendered: false, variants: { male: 6, female: 6 }, firstIndex: 1, token: 'ElbowAttachLeft', merge: 'attachment', required: false },
  ElbowAttachRight: { gendered: false, variants: { male: 6, female: 6 }, firstIndex: 1, token: 'ElbowAttachRight', merge: 'attachment', required: false },
  KneeAttachLeft: { gendered: false, variants: { male: 11, female: 11 }, firstIndex: 1, token: 'KneeAttachLeft', merge: 'attachment', required: false },
  KneeAttachRight: { gendered: false, variants: { male: 11, female: 11 }, firstIndex: 1, token: 'KneeAttachRight', merge: 'attachment', required: false },
  // Capes, pouches and belts -- NOT quivers. The archer's quiver has no socket
  // anywhere in the art and must be synthesised (§9.3).
  BackAttachment: { gendered: false, variants: { male: 15, female: 15 }, firstIndex: 1, token: 'BackAttachment', merge: 'attachment', required: false },
  HipsAttachment: { gendered: false, variants: { male: 12, female: 12 }, firstIndex: 1, token: 'HipsAttachment', merge: 'attachment', required: false },
};

/**
 * Filename token -> slot. The generated catalogue keys entries by the token it found
 * on disk (`Head_No_Elements`), which is not the slot name (`HeadNoElements`).
 */
export const SLOT_BY_TOKEN: ReadonlyMap<string, HeroSlot> = new Map(
  HERO_SLOTS.map((slot) => [SLOT_META[slot].token, slot] as const),
);

export function heroSlotForToken(token: string): HeroSlot | undefined {
  return SLOT_BY_TOKEN.get(token);
}

/** Slots offered to a character of `gender` -- those with at least one mesh. */
export function slotsForGender(gender: Gender): HeroSlot[] {
  return HERO_SLOTS.filter((slot) => SLOT_META[slot].variants[gender] > 0);
}

/** How many meshes fill `slot` for `gender`. */
export function variantCount(slot: HeroSlot, gender: Gender): number {
  return SLOT_META[slot].variants[gender];
}

/** Valid variant indices for `slot` and `gender`, in filename order. */
export function variantIndices(slot: HeroSlot, gender: Gender): number[] {
  const meta = SLOT_META[slot];
  return Array.from({ length: meta.variants[gender] }, (_, i) => meta.firstIndex + i);
}

export function isVariantIndex(slot: HeroSlot, gender: Gender, index: number): boolean {
  const meta = SLOT_META[slot];
  return (
    Number.isInteger(index) &&
    index >= meta.firstIndex &&
    index < meta.firstIndex + meta.variants[gender]
  );
}

/**
 * Source filename for one variant, without extension -- the join between this
 * catalogue and the files on disk. The asset pipeline resolves it; nothing at
 * runtime should ever see it.
 */
export function heroPartFileName(slot: HeroSlot, gender: Gender, index: number): string {
  const meta = SLOT_META[slot];
  const suffix = String(index).padStart(2, '0');
  return meta.gendered
    ? `SK_Chr_${meta.token}_${gender === 'male' ? 'Male' : 'Female'}_${suffix}`
    : `SK_Chr_${meta.token}_${suffix}`;
}

/** Stable catalogue id for one variant, e.g. `Torso/male/07` or `Hair/shared/12`. */
export function variantId(slot: HeroSlot, gender: Gender, index: number): string {
  const scope = SLOT_META[slot].gendered ? gender : 'shared';
  return `${slot}/${scope}/${String(index).padStart(2, '0')}`;
}

export interface HeroOptionCounts {
  /** Distinct `SK_Chr_*.fbx` files across every slot. */
  partFiles: number;
  /** Options a male character can choose from, summed over all slots. */
  male: number;
  /** Options a female character can choose from. */
  female: number;
}

export function heroOptionCounts(): HeroOptionCounts {
  let partFiles = 0;
  let male = 0;
  let female = 0;
  for (const slot of HERO_SLOTS) {
    const meta = SLOT_META[slot];
    male += meta.variants.male;
    female += meta.variants.female;
    // A shared slot's two per-gender numbers count the same files once.
    partFiles += meta.gendered ? meta.variants.male + meta.variants.female : meta.variants.male;
  }
  return { partFiles, male, female };
}

/**
 * Measured against the shipped pack. The asset pipeline asserts the generated
 * catalogue against these (step 3.2): a mismatch means the pack contents changed or
 * a slot was mis-parsed, and both are worth failing loudly over.
 */
export const EXPECTED_HERO_OPTION_COUNTS: Readonly<HeroOptionCounts> = {
  partFiles: 720,
  male: 463,
  female: 442,
};

/** Empty when `SLOT_META` still describes the pack. Run by `pipeline check`. */
export function verifyHeroSlotCounts(): string[] {
  const problems: string[] = [];

  if (HERO_SLOTS.length !== 28) {
    problems.push(`expected 28 hero slots, found ${HERO_SLOTS.length}`);
  }

  const actual = heroOptionCounts();
  for (const key of ['partFiles', 'male', 'female'] as const) {
    if (actual[key] !== EXPECTED_HERO_OPTION_COUNTS[key]) {
      problems.push(`${key}: expected ${EXPECTED_HERO_OPTION_COUNTS[key]}, computed ${actual[key]}`);
    }
  }

  for (const slot of HERO_SLOTS) {
    const meta = SLOT_META[slot];
    if (!meta.gendered && meta.variants.male !== meta.variants.female) {
      problems.push(`${slot}: shared slot has different per-gender counts`);
    }
    const twin = meta.exclusiveWith;
    if (twin && SLOT_META[twin].exclusiveWith !== slot) {
      problems.push(`${slot}: exclusiveWith "${twin}" is not reciprocal`);
    }
  }

  return problems;
}
